import { Add, DeleteOutline, DynamicFeedOutlined } from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  LinearProgress,
  Radio,
  Switch,
  RadioGroup,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../../../../services/api";
import { notifyOnPage } from "../../../../utils/notifyOnPage";

export interface HostItem {
  host: string;
  item: string[];
}
interface HostErrorType {
  perfilIdx: number;
  variavelIdx: number;
  hostIdx: number;
  message: string;
  field?: "host" | "item" | "custom";
}

interface ConfigureHostsProps {
  hostData: HostDataType[];
  setHostData: Dispatch<SetStateAction<HostDataType[]>>;
  relatorioEditData?: any;
  isEditDataProcessed?: boolean;
  setIsEditDataProcessed?: Dispatch<SetStateAction<boolean>>;
  errors?: HostErrorType[];
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const ConfigureHosts = ({
  hostData,
  setHostData,
  relatorioEditData,
  isEditDataProcessed = false,
  setIsEditDataProcessed,
  errors,
}: ConfigureHostsProps) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [hosts, setHosts] = useState<{ id: string; nome: string }[]>([]);
  const [itemsLoadingHostId, setItemsLoadingHostId] = useState<string | null>(
    null,
  );
  const [avaibleItems, setAvaibleItems] = useState<
    Map<string, { itemId: string; name: string }[]>
  >(new Map());

  const [replicarHosts, setReplicarHosts] = useState(false);
  const hasAggreate = useRef(new Map<string, boolean>());
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const fetchHosts = async () => {
      try {
        setLoading(true);
        const response = await api.get("/easyReport/HostName/find");
        setHosts(response.data);
      } catch (error: any) {
        notifyOnPage(
          window.location.pathname,
          error?.response?.data?.error || "Erro ao carregar hosts",
          "error",
          3000,
        );
      } finally {
        setLoading(false);
      }
    };
    fetchHosts();

    const hostRequests: {
      hostId: string;
      item: { key: string; aplicacaoName: string }[];
    }[] = [];

    hostData.forEach((perfil) => {
      perfil.variaveis?.forEach((variavel) => {
        if (variavel.titulo === "Agregar hosts") {
          hasAggreate.current.set(perfil.perfilId, false);
        }
        variavel.hosts?.forEach((host) => {
          if (host.host && variavel.item) {
            hostRequests.push({ hostId: host.host, item: variavel.item });
          }
        });
      });
    });

    hostRequests.forEach(async ({ hostId, item }) => {
      const compositeKey = getCompositeKey(hostId, item);
      if (!avaibleItems.has(compositeKey)) {
        try {
          const items = await api.post("/easyReport/v2/findItemsHostsKey", {
            hostId,
            key_: item.map((k) => k.key),
          });
          setAvaibleItems((prev) => {
            const newMap = new Map(prev);
            newMap.set(compositeKey, items.data);
            return newMap;
          });
        } catch (error: any) {
          notifyOnPage(
            window.location.pathname,
            error?.response?.data?.error || "Erro ao carregar items",
            "error",
            3000,
          );
        }
      }
    });
  }, []);

  // Carrega os dados de edição
  useEffect(() => {
    if (!relatorioEditData || isEditDataProcessed) return;
    const loadEditData = async () => {
      try {
        const newHostData: HostDataType[] = [];
        const loadedProfileIds = new Set<string>();

        for (const profile of relatorioEditData.profiles) {
          const matchingPerfil = hostData.find(
            (h) => h.perfilId === profile.id,
          );
          if (!matchingPerfil) continue;
          loadedProfileIds.add(profile.id);

          const updatedVariaveis = await Promise.all(
            matchingPerfil.variaveis?.map(async (variavel) => {
              const editVariavel = profile.variaveis?.find(
                (v: any) => v.id === variavel.id,
              );
              if (!editVariavel) return variavel;
              const updatedHosts = await Promise.all(
                editVariavel.hosts.map(async (editHost: any) => {
                  const compositeKey = getCompositeKey(
                    editHost.hostId.toString(),
                    variavel.item || [],
                  );
                  if (!avaibleItems.has(compositeKey)) {
                    try {
                      const items = await api.post(
                        "/easyReport/v2/findItemsHostsKey",
                        {
                          hostId: editHost.hostId.toString(),
                          key_: variavel.item.map((k) => k.key),
                        },
                      );
                      setAvaibleItems((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(compositeKey, items.data);
                        return newMap;
                      });
                    } catch (error: any) {
                      notifyOnPage(
                        window.location.pathname,
                        error?.response?.data?.error ||
                          "Erro ao carregar items",
                        "error",
                        3000,
                      );
                    }
                  }
                  return {
                    host: editHost.hostId.toString(),
                    item: editHost.items.map((itemId: number) =>
                      itemId.toString(),
                    ),
                  };
                }),
              );
              return {
                ...variavel,
                hosts: updatedHosts,
              };
            }) || [],
          );
          newHostData.push({
            ...matchingPerfil,
            variaveis: updatedVariaveis,
          });
        }
        // Adiciona os perfis adicionados antes da inicialização
        const newProfiles = hostData.filter(
          (h) => !loadedProfileIds.has(h.perfilId),
        );
        newHostData.push(...newProfiles);

        if (newHostData.length > 0) {
          setHostData(newHostData);
          if (setIsEditDataProcessed) {
            setIsEditDataProcessed(true);
          }
        }
      } catch (error: any) {
        notifyOnPage(
          window.location.pathname,
          error?.response?.data?.error || "Erro ao carregar dados do relatório",
          "error",
          3000,
        );
      }
    };
    loadEditData();
  }, [relatorioEditData, isEditDataProcessed]);

  function getCompositeKey(hostId: string, items: { key: string }[]): string {
    const itemKeys = items
      .map((k) => k.key)
      .sort()
      .join(",");
    return `${hostId}:${itemKeys}`;
  }

  const avaibleHosts = useMemo(() => hosts, [hosts]);

  const handleAddHost = (perfilIdx: number, variavelIdx: number) => {
    setHostData((prev) =>
      prev.map((perfil, idx) =>
        idx === perfilIdx
          ? {
              ...perfil,
              variaveis: perfil.variaveis?.map((variavel, vIdx) =>
                vIdx === variavelIdx
                  ? {
                      ...variavel,
                      hosts: [
                        ...(variavel.hosts || []),
                        { host: "", item: [] },
                      ],
                    }
                  : variavel,
              ),
            }
          : perfil,
      ),
    );
  };

  const isHostVariavel = (v: NonNullable<HostDataType["variaveis"]>[number]) =>
    v.item?.[0]?.aplicacaoName !== "CUSTOM";

  const canAddHost = (
    perfil: HostDataType,
    v: NonNullable<HostDataType["variaveis"]>[number],
  ) => v.multiple && hasAggreate.current.get(perfil.perfilId) !== false;

  const hostVariaveisCount = hostData.reduce(
    (acc, p) => acc + (p.variaveis?.filter(isHostVariavel).length || 0),
    0,
  );

  const handleRemoveHost = (
    perfilIdx: number,
    variavelIdx: number,
    hostIdx: number,
  ) => {
    const removedHost =
      hostData[perfilIdx]?.variaveis?.[variavelIdx]?.hosts?.[hostIdx]?.host;
    setHostData((prev) =>
      prev.map((perfil, pIdx) => ({
        ...perfil,
        variaveis: perfil.variaveis?.map((variavel, vIdx) => {
          const isSource = pIdx === perfilIdx && vIdx === variavelIdx;
          const isMirror =
            replicarHosts &&
            !!removedHost &&
            isHostVariavel(variavel) &&
            (variavel.hosts?.length || 0) > 1 &&
            variavel.hosts?.[hostIdx]?.host === removedHost;
          if (!isSource && !isMirror) return variavel;
          return {
            ...variavel,
            hosts: variavel.hosts?.filter((_, i) => i !== hostIdx) || [],
          };
        }),
      })),
    );
  };

  const styles = {
    container: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
    },
    perfilCard: {
      border: `1px solid ${theme.palette.stroke}`,
      borderRadius: 2,
      overflow: "hidden",
    },
    perfilHeader: {
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderBottom: `1px solid ${theme.palette.stroke}`,
      backgroundColor: alpha(theme.palette.text.primary, 0.03),
    },
    perfilTitle: {
      fontWeight: 600,
      fontSize: 15,
      textTransform: "capitalize",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    perfilBody: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      px: 1.5,
      pt: 1.25,
      pb: 1.5,
    },
    customGrid: {
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
      gap: 1.5,
      pb: 2,
      borderBottom: `1px dashed ${theme.palette.stroke}`,
    },
    fieldLabel: {
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.text.secondary,
      mb: 0.5,
      textTransform: "capitalize",
    },
    variavelBox: {
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
    },
    variavelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
    },
    variavelTitle: {
      fontWeight: 600,
      fontSize: 14,
      textTransform: "capitalize",
    },
    countChip: {
      height: 22,
      fontSize: 12,
      fontWeight: 500,
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
    hostGrid: {
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr) auto",
        md: "minmax(0, 1fr) minmax(0, 1fr) 34px",
      },
      columnGap: 1,
      rowGap: 0.75,
      alignItems: "start",
    },
    columnHeader: {
      display: { xs: "none", md: "block" },
      fontSize: 12,
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
    fieldBox: { position: "relative", minWidth: 0 },
    hostField: { gridColumn: { xs: "1 / -1", md: "auto" } },
    progress: {
      position: "absolute",
      top: 0,
      left: 4,
      right: 4,
      height: "2px",
      borderRadius: 1,
    },
    deleteButton: {
      mt: "1px",
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.08),
      },
    },
    rowDivider: {
      display: { xs: "block", md: "none" },
      gridColumn: "1 / -1",
      borderBottom: `1px dashed ${theme.palette.stroke}`,
      my: 0.25,
    },
    addButton: {
      mr: { md: "42px" },
      height: 30,
      fontSize: 13,
      borderRadius: 1.5,
      border: `1.5px dashed ${theme.palette.stroke}`,
      color: theme.palette.text.secondary,
      textTransform: "none",
      fontWeight: 500,
      "&:hover": {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.04),
      },
    },
    ellipsisInput: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: 14,
    } as const,
    compactField: {
      "& .MuiInputBase-root": { py: "3px", minHeight: 36 },
    },
  };

  const handleCustomInput = (
    perfilIdx: number,
    variavelIdx: number,
    newValue: string,
  ) => {
    setHostData((prev) =>
      prev.map((p, pIdx) =>
        pIdx === perfilIdx
          ? {
              ...p,
              variaveis: p.variaveis?.map((v, vIdx) => {
                if (v.titulo === "Agregar hosts") {
                  hasAggreate.current.set(p.perfilId, newValue === "1");
                }
                return vIdx === variavelIdx
                  ? {
                      ...v,
                      hosts: v.hosts?.map((h) => ({
                        ...h,
                        host: newValue ? newValue : "",
                        item: ["1"],
                      })),
                    }
                  : v;
              }),
            }
          : p,
      ),
    );
  };

  const selectSingleItem = (
    hostId: string,
    item: { key: string }[],
    items: { itemId: string; name: string }[],
  ) => {
    if (items.length !== 1) return;
    const compositeKey = getCompositeKey(hostId, item);
    setHostData((prev) =>
      prev.map((p) => ({
        ...p,
        variaveis: p.variaveis?.map((v) =>
          getCompositeKey(hostId, v.item || []) === compositeKey
            ? {
                ...v,
                hosts: v.hosts?.map((h) =>
                  h.host === hostId && h.item.length === 0
                    ? { ...h, item: [items[0].itemId] }
                    : h,
                ),
              }
            : v,
        ),
      })),
    );
  };

  const loadItems = async (
    hostId: string,
    item: { key: string; aplicacaoName: string }[],
    signal?: AbortSignal,
  ) => {
    const compositeKey = getCompositeKey(hostId, item);
    const cached = avaibleItems.get(compositeKey);
    if (cached) {
      selectSingleItem(hostId, item, cached);
      return;
    }
    try {
      setItemsLoadingHostId(hostId);
      const items = await api.post(
        "/easyReport/v2/findItemsHostsKey",
        {
          hostId,
          key_: item.map((k) => k.key),
        },
        { signal },
      );
      setAvaibleItems((prev) => {
        const newMap = new Map(prev);
        newMap.set(compositeKey, items.data);
        return newMap;
      });
      selectSingleItem(hostId, item, items.data);
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      notifyOnPage(
        window.location.pathname,
        error?.response?.data?.error || "Erro ao carregar items",
        "error",
        3000,
      );
    } finally {
      setItemsLoadingHostId(null);
    }
  };

  const handleHostChange = async (
    perfilIdx: number,
    variavelIdx: number,
    hostIdx: number,
    newValue: { id: string; nome: string } | null,
    item: { key: string; aplicacaoName: string }[],
  ) => {
    const hostId = newValue ? newValue.id : "";
    const mirrorItems = new Map<
      string,
      { key: string; aplicacaoName: string }[]
    >();

    setHostData((prev) =>
      prev.map((p, pIdx) => ({
        ...p,
        variaveis: p.variaveis?.map((v, vIdx) => {
          const isSource = pIdx === perfilIdx && vIdx === variavelIdx;
          if (!isSource && !(replicarHosts && isHostVariavel(v))) return v;
          const hosts = [...(v.hosts || [])];
          if (!isSource && hostIdx >= hosts.length) {
            if (!canAddHost(p, v)) return v;
            while (hosts.length <= hostIdx) hosts.push({ host: "", item: [] });
          }
          if (!isSource && hosts[hostIdx].host === hostId) return v;
          hosts[hostIdx] = isSource
            ? { ...hosts[hostIdx], host: hostId }
            : { host: hostId, item: [] };
          if (!isSource && hostId) {
            mirrorItems.set(getCompositeKey(hostId, v.item || []), v.item);
          }
          return { ...v, hosts };
        }),
      })),
    );
    if (!newValue) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    mirrorItems.delete(getCompositeKey(hostId, item));
    mirrorItems.forEach((mirrorItem) => loadItems(hostId, mirrorItem));
    await loadItems(hostId, item, controller.signal);
  };

  const handleItemChange = (
    perfilIdx: number,
    variavelIdx: number,
    hostIdx: number,
    newValue: { itemId: string; name: string }[],
  ) => {
    setHostData((prev) =>
      prev.map((perfil, pIdx) =>
        pIdx === perfilIdx
          ? {
              ...perfil,
              variaveis: perfil.variaveis?.map((variavel, vIdx) =>
                vIdx === variavelIdx
                  ? {
                      ...variavel,
                      hosts: variavel.hosts?.map((host, hIdx) =>
                        hIdx === hostIdx
                          ? {
                              ...host,
                              item: newValue.map((item) => item.itemId),
                            }
                          : host,
                      ),
                    }
                  : variavel,
              ),
            }
          : perfil,
      ),
    );
  };

  const getFieldError = (
    perfilIdx: number,
    variavelIdx: number,
    hostIdx: number,
    field?: "host" | "item" | "custom",
  ) => {
    if (!errors || errors.length === 0) return undefined;
    if (!field) return undefined;
    return errors.find(
      (e) =>
        e.perfilIdx === perfilIdx &&
        e.variavelIdx === variavelIdx &&
        e.hostIdx === hostIdx &&
        e.field === field,
    );
  };
  return (
    <Box sx={styles.container}>
      {hostVariaveisCount > 1 && (
        <Box
          onClick={() => setReplicarHosts((prev) => !prev)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            cursor: "pointer",
            userSelect: "none",
            border: `1.5px solid ${replicarHosts ? theme.palette.primary.main : theme.palette.stroke}`,
            backgroundColor: replicarHosts
              ? alpha(theme.palette.primary.main, 0.06)
              : "transparent",
            transition: "all .2s",
            "&:hover": {
              borderColor: replicarHosts
                ? theme.palette.primary.main
                : theme.palette.text.disabled,
            },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: replicarHosts
                ? theme.palette.common.white
                : "text.secondary",
              backgroundColor: replicarHosts
                ? theme.palette.primary.main
                : alpha(theme.palette.text.primary, 0.06),
              transition: "all .2s",
            }}
          >
            <DynamicFeedOutlined sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500 }}>
            Replicar hosts em todas as funções
          </Typography>
          <Switch
            size="small"
            checked={replicarHosts}
            inputProps={{ "aria-label": "Replicar hosts em todas as funções" }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setReplicarHosts(e.target.checked)}
          />
        </Box>
      )}
      {hostData.map((perfil, perfilIdx) => (
        <Box key={perfil.perfilId} sx={styles.perfilCard}>
          <Box sx={styles.perfilHeader}>
            <Typography sx={styles.perfilTitle}>
              {perfil.perfilTitle}
            </Typography>
          </Box>

          <Box sx={styles.perfilBody}>
            {perfil.variaveis?.some(
              (v) => v.item?.[0].aplicacaoName === "CUSTOM",
            ) && (
              <Box sx={styles.customGrid}>
                {perfil.variaveis?.map((variavel, variavelIdx) => {
                  if (variavel.item[0].aplicacaoName !== "CUSTOM") return null;
                  const customError = getFieldError(
                    perfilIdx,
                    variavelIdx,
                    0,
                    "custom",
                  );
                  return (
                    <Box key={variavel.id}>
                      <Typography sx={styles.fieldLabel}>
                        {variavel.titulo}
                      </Typography>
                      {variavel.item[0]?.key === "boolean" ? (
                        <FormControl error={!!customError}>
                          <RadioGroup
                            row
                            value={
                              variavel.hosts?.[0]?.host === "1"
                                ? "1"
                                : variavel.hosts?.[0]?.host === "0"
                                  ? "0"
                                  : ""
                            }
                            onChange={(e) =>
                              handleCustomInput(
                                perfilIdx,
                                variavelIdx,
                                e.target.value,
                              )
                            }
                          >
                            <FormControlLabel
                              value="1"
                              control={<Radio size="small" />}
                              label="Verdadeiro"
                            />
                            <FormControlLabel
                              value="0"
                              control={<Radio size="small" />}
                              label="Falso"
                            />
                          </RadioGroup>
                          {customError && (
                            <FormHelperText>
                              {customError.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      ) : (
                        <TextField
                          value={variavel.hosts?.[0]?.host ?? ""}
                          onChange={(e) => {
                            let value = e.target.value.replace(/,/g, ".");
                            if (/^(\d+(\.\d*)?|\.\d*)?$/.test(value)) {
                              handleCustomInput(perfilIdx, variavelIdx, value);
                            }
                          }}
                          size="small"
                          fullWidth
                          error={!!customError}
                          helperText={customError?.message}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}

            {perfil.variaveis?.map((variavel, variavelIdx) => {
              if (variavel.item?.[0].aplicacaoName === "CUSTOM") return null;
              const hostCount =
                variavel.hosts?.filter((h) => h.host).length || 0;
              const canDelete = (variavel.hosts?.length || 0) > 1;
              return (
                <Box key={variavel.id} sx={styles.variavelBox}>
                  <Box sx={styles.variavelHeader}>
                    <Typography sx={styles.variavelTitle}>
                      {variavel.titulo}
                    </Typography>
                    {hostCount > 0 && (
                      <Chip
                        size="small"
                        label={`${hostCount} ${hostCount === 1 ? "host" : "hosts"}`}
                        sx={styles.countChip}
                      />
                    )}
                  </Box>

                  <Box sx={styles.hostGrid}>
                    <Typography sx={styles.columnHeader}>Host</Typography>
                    <Typography sx={styles.columnHeader}>Item</Typography>
                    <Box sx={{ display: { xs: "none", md: "block" } }} />

                    {variavel.hosts?.map((host, hostIdx) => {
                      const hostError = getFieldError(
                        perfilIdx,
                        variavelIdx,
                        hostIdx,
                        "host",
                      );
                      const itemError = getFieldError(
                        perfilIdx,
                        variavelIdx,
                        hostIdx,
                        "item",
                      );
                      const itemOptions =
                        avaibleItems.get(
                          getCompositeKey(host.host, variavel.item || []),
                        ) || [];

                      return (
                        <Fragment key={hostIdx + "host"}>
                          {hostIdx > 0 && <Box sx={styles.rowDivider} />}
                          <Box sx={{ ...styles.fieldBox, ...styles.hostField }}>
                            <Autocomplete
                              value={
                                avaibleHosts.find((h) => h.id === host.host) ||
                                null
                              }
                              options={avaibleHosts}
                              getOptionLabel={(option) => option.nome}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                              loading={loading}
                              size="small"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Host"
                                  sx={styles.compactField}
                                  error={!!hostError}
                                  helperText={hostError?.message}
                                  inputProps={{
                                    ...params.inputProps,
                                    style: styles.ellipsisInput,
                                  }}
                                />
                              )}
                              onChange={(_, newValue) => {
                                handleHostChange(
                                  perfilIdx,
                                  variavelIdx,
                                  hostIdx,
                                  newValue,
                                  variavel.item,
                                );
                              }}
                            />
                            <Fade in={loading} unmountOnExit>
                              <LinearProgress sx={styles.progress} />
                            </Fade>
                          </Box>

                          <Box
                            sx={{
                              ...styles.fieldBox,
                              ...(!canDelete && styles.hostField),
                            }}
                          >
                            <Autocomplete
                              multiple
                              disableCloseOnSelect
                              value={itemOptions.filter((item) =>
                                host.item.includes(item.itemId),
                              )}
                              limitTags={1}
                              size="small"
                              options={itemOptions}
                              getOptionLabel={(option) => option.name}
                              loading={itemsLoadingHostId === host.host}
                              disabled={!host.host}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={
                                    host.item.length ? undefined : "Item"
                                  }
                                  sx={styles.compactField}
                                  error={!!itemError}
                                  helperText={itemError?.message}
                                  inputProps={{
                                    ...params.inputProps,
                                    style: styles.ellipsisInput,
                                  }}
                                />
                              )}
                              onChange={(_, newValue) => {
                                if (newValue) {
                                  handleItemChange(
                                    perfilIdx,
                                    variavelIdx,
                                    hostIdx,
                                    newValue,
                                  );
                                }
                              }}
                              renderOption={(props, option, { selected }) => {
                                const { key, ...optionProps } = props;
                                return (
                                  <li key={key} {...optionProps}>
                                    <Checkbox
                                      icon={icon}
                                      checkedIcon={checkedIcon}
                                      style={{ marginRight: 8 }}
                                      checked={selected}
                                    />
                                    {option.name}
                                  </li>
                                );
                              }}
                            />
                            <Fade
                              in={itemsLoadingHostId === host.host || loading}
                              unmountOnExit
                            >
                              <LinearProgress sx={styles.progress} />
                            </Fade>
                          </Box>

                          {canDelete ? (
                            <IconButton
                              size="small"
                              aria-label="Remover host"
                              sx={styles.deleteButton}
                              onClick={() =>
                                handleRemoveHost(
                                  perfilIdx,
                                  variavelIdx,
                                  hostIdx,
                                )
                              }
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          ) : (
                            <Box
                              sx={{ display: { xs: "none", md: "block" } }}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </Box>

                  {canAddHost(perfil, variavel) && (
                    <Button
                      startIcon={<Add />}
                      sx={styles.addButton}
                      onClick={() => handleAddHost(perfilIdx, variavelIdx)}
                    >
                      Adicionar Host
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
//export default ConfigureHosts;
